import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../axiosConfig';
import {
  Sparkles, X, Send, ExternalLink, TrendingUp, Package, Users,
  FileText, ShoppingCart, MessageSquare, HelpCircle, Settings
} from 'lucide-react';
import '../../styles/ai-chat.css';

// First 4 are shown as the empty-state grid. Mix of support (open to every
// tier via ai.support intent) and business (PRO+ via ai.predictions) so
// FREE merchants see examples they can actually use without an upgrade.
const SUGGESTIONS = [
  { Icon: HelpCircle,  title: 'Help',      sub: 'How do I set up Paystack?' },
  { Icon: TrendingUp,  title: 'Sales',     sub: 'How are my sales this month?' },
  { Icon: Settings,    title: 'Setup',     sub: 'How do I add my first product?' },
  { Icon: ShoppingCart,title: 'Orders',    sub: 'Show recent orders' },
  { Icon: Package,     title: 'Inventory', sub: 'Which products are low on stock?' },
  { Icon: Users,       title: 'Customers', sub: 'Who are my top customers?' },
  { Icon: FileText,    title: 'Report',    sub: 'Generate a business report' },
  { Icon: Sparkles,    title: 'Insights',  sub: 'What should I focus on this week?' }
];

// Session storage keys — Shell is mounted per-page, so the launcher
// unmounts on navigation. sessionStorage preserves the in-flight chat
// across nav within a session and clears when the tab closes.
const STORAGE_MESSAGES = 'revstar_reva_messages';
const STORAGE_CONVERSATION_ID = 'revstar_reva_conversation_id';

const loadStoredMessages = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_MESSAGES);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Re-hydrate Date instances — JSON.parse leaves them as strings.
    return parsed.map((m) => ({
      ...m,
      timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
    }));
  } catch {
    return [];
  }
};

const loadStoredConversationId = () => {
  try {
    return sessionStorage.getItem(STORAGE_CONVERSATION_ID) || null;
  } catch {
    return null;
  }
};

const formatTime = (date) => {
  if (!(date instanceof Date)) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Linkify visible order/product identifiers and inline KB citations in an
// assistant response. Two patterns share one walk so we don't double-render.
// - Order IDs:  #ORD-12345 / #POS-...
// - KB cites:   [/knowledgebase/some-slug]  (emitted by the support intent)
const renderAssistantContent = (text) => {
  if (!text) return null;
  const pattern = /#((?:ORD|POS|WA|WHA)[A-Z0-9-]+|[A-Z]{2,}-[A-Z0-9-]{2,})|\[\/knowledgebase\/([a-z0-9-]+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const orderNumber = match[1];
    const kbSlug = match[2];
    if (orderNumber) {
      parts.push(
        <a
          key={`l-${key++}`}
          href={`/orders?q=${encodeURIComponent(orderNumber)}`}
          className="aichat-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          #{orderNumber}
        </a>
      );
    } else if (kbSlug) {
      parts.push(
        <a
          key={`l-${key++}`}
          href={`/knowledgebase/${kbSlug}`}
          className="aichat-link aichat-link-cite"
          target="_blank"
          rel="noopener noreferrer"
          title={`Open article: ${kbSlug}`}
        >
          ↗
        </a>
      );
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const AIChatLauncher = () => {
  // Launcher is visible to every tier — gating moved to per-intent inside
  // enhancedAIStoreAssistant.handleEnhancedMerchantQuery. FREE merchants
  // can ask support questions; business/content intents return an upgrade
  // prompt in the response stream.
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Hydrate from sessionStorage so the chat survives page navigation —
  // Shell is mounted per-page, so this component unmounts on every nav.
  const [messages, setMessages] = useState(loadStoredMessages);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(loadStoredConversationId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Persist messages + conversationId on every change.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_MESSAGES, JSON.stringify(messages));
    } catch { /* quota or disabled — non-blocking */ }
  }, [messages]);
  useEffect(() => {
    try {
      if (conversationId) sessionStorage.setItem(STORAGE_CONVERSATION_ID, conversationId);
      else sessionStorage.removeItem(STORAGE_CONVERSATION_ID);
    } catch { /* non-blocking */ }
  }, [conversationId]);

  // Hide entirely on the dedicated AI page — the page IS the chat
  const onAIPage = location.pathname === '/ai-analytics';

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const sendMessage = useCallback(async (text) => {
    const messageText = (text ?? input).trim();
    if (!messageText || sending) return;

    const userMsg = { role: 'user', content: messageText, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    let convId = conversationId;
    try {
      if (!convId) {
        const created = await axios.post('/ai/conversations', { initialMessage: messageText });
        if (created.data?.success) {
          convId = created.data.conversation.id;
          setConversationId(convId);
        }
      } else {
        await axios.post(`/ai/conversations/${convId}/messages`, { role: 'user', content: messageText });
      }

      const sessionId = `launcher_${Date.now()}`;
      const response = await axios.post('/ai/enhanced/merchant/chat', {
        query: messageText,
        sessionId,
        conversationId: convId
      });

      const aiMsg = {
        role: 'assistant',
        content: response.data?.response || 'No response from AI',
        timestamp: new Date(),
        metadata: {
          reportUrl: response.data?.reportUrl || null,
          dataPoints: response.data?.dataPoints || null,
          // RAG citations from the support intent — array of {slug, title}.
          citations: Array.isArray(response.data?.citations) ? response.data.citations : []
        }
      };
      setMessages(prev => [...prev, aiMsg]);

      if (convId) {
        try {
          await axios.post(`/ai/conversations/${convId}/messages`, {
            role: 'assistant',
            content: aiMsg.content,
            metadata: aiMsg.metadata
          });
        } catch (e) { /* non-blocking */ }
      }

      if (aiMsg.metadata.reportUrl) {
        setTimeout(() => window.open(aiMsg.metadata.reportUrl, '_blank'), 600);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I hit an error. Try again in a moment.',
        timestamp: new Date()
      }]);
    } finally {
      setSending(false);
    }
  }, [conversationId, input, sending]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openFullPage = () => {
    setOpen(false);
    navigate(conversationId ? `/ai-analytics?conv=${conversationId}` : '/ai-analytics');
  };

  if (onAIPage) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          className="aichat-launch__bubble"
          onClick={() => setOpen(true)}
          aria-label="Open AI assistant"
        >
          <Sparkles aria-hidden />
        </button>
      )}

      {open && (
        <div className="aichat-launch__panel" role="dialog" aria-label="AI assistant">
          <header className="aichat-head">
            <div className="aichat-head__avatar">
              <Sparkles size={18} aria-hidden />
              <span className="aichat-head__avatar-status" aria-hidden />
            </div>
            <div className="aichat-head__text">
              <div className="aichat-head__name">Reva</div>
              <div className="aichat-head__status">Online</div>
            </div>
            <div className="aichat-head__actions">
              <button
                type="button"
                className="aichat-head__btn aichat-head__btn--text"
                onClick={openFullPage}
                title="Open full page"
              >
                <ExternalLink size={12} aria-hidden /> Full view
              </button>
              <button
                type="button"
                className="aichat-head__btn"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          </header>

          {messages.length === 0 ? (
            <div className="aichat-empty">
              <div className="aichat-empty__hello">What can I do?</div>
              <h2 className="aichat-empty__title">Ask me about your business</h2>
              <p className="aichat-empty__sub">Pick a quick prompt or type your own.</p>
              <div className="aichat-empty__grid">
                {SUGGESTIONS.slice(0, 4).map(({ Icon, title, sub }) => (
                  <button
                    key={title}
                    type="button"
                    className="aichat-suggestion"
                    onClick={() => sendMessage(sub)}
                  >
                    <span className="aichat-suggestion__icon"><Icon size={14} aria-hidden /></span>
                    <span className="aichat-suggestion__title">{title}</span>
                    <span className="aichat-suggestion__sub">{sub}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="aichat-messages">
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={`aichat-msg aichat-msg--${m.role === 'user' ? 'user' : 'assistant'}`}
                >
                  {m.role !== 'user' && (
                    <span className="aichat-msg__avatar" aria-hidden>
                      <Sparkles size={14} />
                    </span>
                  )}
                  <div className="aichat-msg__body">
                    <div className="aichat-msg__bubble">
                      {m.role === 'assistant' && (
                        <div className="aichat-msg__label">
                          <Sparkles size={10} aria-hidden /> AI analysis
                        </div>
                      )}
                      {m.role === 'assistant' ? renderAssistantContent(m.content) : m.content}
                    </div>
                    {m.metadata?.reportUrl && (
                      <div className="aichat-rich">
                        <div className="aichat-rich__head">
                          <span className="aichat-rich__title">
                            <FileText size={11} aria-hidden /> Report ready
                          </span>
                          <a href={m.metadata.reportUrl} target="_blank" rel="noopener noreferrer" className="aichat-rich__cta">
                            <ExternalLink size={11} aria-hidden /> Open
                          </a>
                        </div>
                      </div>
                    )}
                    {m.metadata?.citations?.length > 0 && (
                      <div className="aichat-cites">
                        <div className="aichat-cites__label">Related articles</div>
                        <div className="aichat-cites__list">
                          {m.metadata.citations.map((c) => (
                            <a
                              key={c.slug}
                              href={`/knowledgebase/${c.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="aichat-cites__item"
                            >
                              <FileText size={11} aria-hidden />
                              <span>{c.title}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {m.timestamp && (
                      <div className="aichat-msg__time">{formatTime(m.timestamp)}</div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="aichat-msg aichat-msg--assistant">
                  <span className="aichat-msg__avatar" aria-hidden>
                    <Sparkles size={14} />
                  </span>
                  <div className="aichat-msg__body">
                    <div className="aichat-msg__bubble" style={{ padding: 0, border: 'none', background: 'transparent' }}>
                      <span className="aichat-typing" aria-hidden>
                        <span /><span /><span />
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          <div className="aichat-composer">
            {messages.length > 0 && (
              <div className="aichat-composer__chips">
                <button type="button" className="aichat-composer__chip" onClick={() => sendMessage('Show recent orders')}>
                  <ShoppingCart size={11} aria-hidden /> Recent orders
                </button>
                <button type="button" className="aichat-composer__chip" onClick={() => sendMessage('Which products are low on stock?')}>
                  <Package size={11} aria-hidden /> Low stock
                </button>
                <button type="button" className="aichat-composer__chip" onClick={() => sendMessage('Generate a PDF business report')}>
                  <FileText size={11} aria-hidden /> Report
                </button>
              </div>
            )}
            <div className="aichat-composer__row">
              <textarea
                ref={inputRef}
                className="aichat-composer__input"
                placeholder="Ask anything…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                rows={1}
                disabled={sending}
              />
              <button
                type="button"
                className="aichat-composer__send"
                onClick={() => sendMessage()}
                disabled={!input.trim() || sending}
                aria-label="Send"
              >
                <Send size={14} aria-hidden />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatLauncher;
